import COURSE_TYPES from './types';
import {
	type ICourse,
	type IInstitution,
	type IMetadata,
	type ISection,
} from 'src/models';
import { type State } from './reducer';
import { types } from 'util';

function initiaState() {
	return {
		type: COURSE_TYPES.INITIAL_STATE,
	};
}

export type Type = keyof State | string;

function set(type: Type, value: any) {
	return {
		type: COURSE_TYPES.SET,
		payload: { type, value },
	};
}

function getAll(page?: number, pageSize?: number) {
	return {
		type: COURSE_TYPES.GET_ALL,
		payload: { page, pageSize },
	};
}

function setAll(data: ICourse[], metadata?: IMetadata) {
	return {
		type: COURSE_TYPES.SET_ALL,
		payload: { data, metadata },
	};
}

function getOne(id: number, userId?: string) {
	return {
		type: COURSE_TYPES.GET_ONE,
		payload: { id, userId },
	};
}

function setOne(course: ICourse) {
	return {
		type: COURSE_TYPES.SET_ONE,
		payload: { course },
	};
}

function create(course: ICourse) {
	return {
		type: COURSE_TYPES.CREATE,
		payload: { course },
	};
}

function update(course: ICourse) {
	return {
		type: COURSE_TYPES.UPDATE,
		payload: { course },
	};
}

function remove(id: number) {
	return {
		type: COURSE_TYPES.REMOVE,
		payload: { id },
	};
}

function getExam(id: number, sectionId?: number) {
	return {
		type: COURSE_TYPES.GET_EXAM,
		payload: { id, sectionId },
	};
}

function setExam(exam: ICourse) {
	return {
		type: COURSE_TYPES.SET_EXAM,
		payload: { exam },
	};
}

function setAnsweredSection(section?: ISection, isSaved?: boolean) {
	return {
		type: COURSE_TYPES.SET_ANSWERED_SECTION,
		payload: { section, isSaved },
	};
}

function getInstitutionCourses(institutionId: number) {
	return {
		type: COURSE_TYPES.GET_INSTITUTION_COURSES,
		payload: { institutionId },
	};
}

function setInstitutionCourses(institutionCourses: any[]) {
	return {
		type: COURSE_TYPES.SET_INSTITUTION_COURSES,
		payload: { institutionCourses },
	};
}

function getUsersCourses(institutionId: number, courseId: number) {
	return {
		type: COURSE_TYPES.GET_USERS_COURSES,
		payload: { institutionId, courseId },
	};
}

function saveUsersCourses(usersCourses: any[]) {
	return {
		type: COURSE_TYPES.SAVE_USERS_COURSES,
		payload: { usersCourses },
	};
}

function getByUser(userId: string) {
	return {
		type: COURSE_TYPES.GET_BY_USER,
		payload: { userId },
	};
}
function getByInstitution(institutionId: number) {
	return {
		type: COURSE_TYPES.GET_BY_INSTITUTION,
		payload: { institutionId },
	};
}

function courseStarted(userId: string, courseId: number) {
	return {
		type: COURSE_TYPES.COURSE_STARTED,
		payload: { userId, courseId },
	};
}

function courseFinished(userId: string, courseId: number) {
	return {
		type: COURSE_TYPES.COURSE_FINISHED,
		payload: { userId, courseId },
	};
}

function updateInstructions(course: ICourse) {
	return {
		type: COURSE_TYPES.UPDATE_INSTRUCTIONS,
		payload: { course },
	};
}

export type ReportType = 'all' | 'institution';

function getReport(type: ReportType) {
	return {
		type: COURSE_TYPES.GET_REPORT,
		payload: { type },
	};
}

function setReport(type: ReportType, data: any) {
	return {
		type: COURSE_TYPES.SET_REPORT,
		payload: { type, data },
	};
}

export type ReportDownloadType = 'advances' | 'qualifications';

function downloadReport(
	type: ReportDownloadType,
	institutionId: number,
	courseId: number,
	year?: number,
) {
	return {
		type: COURSE_TYPES.DOWNLOAD_REPORT,
		payload: { type, institutionId, courseId, year },
	};
}

function getExamReview(courseId: number, userId: number) {
	return {
		type: COURSE_TYPES.GET_EXAM_REVIEW,
		payload: { courseId, userId },
	};
}

function setExamReview(data: any[]) {
	return {
		type: COURSE_TYPES.SET_EXAM_REVIEW,
		payload: { data },
	};
}

function getUserCertificate(userId: string, courseId: number) {
	return {
		type: COURSE_TYPES.GET_USER_CERTIFICATE,
		payload: { userId, courseId },
	};
}

function setUserCertificate(data: any) {
	return {
		type: COURSE_TYPES.SET_USER_CERTIFICATE,
		payload: { data },
	};
}

function setReviewInstitution(institution: IInstitution) {
	return {
		type: COURSE_TYPES.SET_REVIEW_INSTITUTION,
		payload: { institution },
	};
}

function setReviewCourse(course: ICourse) {
	return {
		type: COURSE_TYPES.SET_REVIEW_COURSE,
		payload: { course },
	};
}

function downloadUserExam(userId: string, courseId: number) {
	return {
		type: COURSE_TYPES.DOWNLOAD_USER_EXAM,
		payload: { userId, courseId },
	};
}

function downloadAllUsersExam(
	institutionId: number,
	courseId: number,
	year: number,
) {
	return {
		type: COURSE_TYPES.DOWNLOAD_ALL_USERS_EXAM,
		payload: { institutionId, courseId, year },
	};
}

function downloadUsersCertificates(
	institutionId: number,
	courseId: number,
	year: number,
	institutionName: string,
) {
	return {
		type: COURSE_TYPES.DOWNLOAD_USERS_CERTIFICATE,
		payload: { institutionId, courseId, year, institutionName },
	};
}

function downloadUserCertificate(
	userId: number,
	courseId: number,
	userName: string,
	institutionName: string,
	courseName: string,
) {
	return {
		type: COURSE_TYPES.DOWNLOAD_USER_CERTIFICATE,
		payload: { userId, courseId, userName, institutionName, courseName },
	};
}

const courseActions = {
	initiaState,
	set,
	getAll,
	setAll,
	getOne,
	setOne,
	create,
	update,
	remove,
	getExam,
	setExam,
	setAnsweredSection,
	getInstitutionCourses,
	setInstitutionCourses,
	getUsersCourses,
	saveUsersCourses,
	getByUser,
	getByInstitution,
	courseStarted,
	courseFinished,
	updateInstructions,
	getReport,
	setReport,
	downloadReport,
	getExamReview,
	setExamReview,
	getUserCertificate,
	setUserCertificate,
	setReviewInstitution,
	setReviewCourse,
	downloadUserExam,
	downloadAllUsersExam,
	downloadUsersCertificates,
	downloadUserCertificate,
};

export default courseActions;
